import type { ColumnDef } from '@tanstack/react-table';
import {
  REMINDER_LOG_STATUSES,
  type TReminderLog,
} from '@src/shared/types/reminder';
import { formatReminderTableDate } from '../shared/reminder-date-format';
import { LogStatusBadge } from '../shared/log-status-badge';
import { getRecipientDisplay } from '../../get-recipient-display';
import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import { Text } from '@src/design-system/text';

export const bulkReminderLogsColumns: ColumnDef<TReminderLog>[] = [
  {
    id: 'sentAt',
    accessorKey: 'created_at',
    header: 'Sent At',
    cell: ({ row }) => (
      <span className='whitespace-nowrap text-sm text-gray-1000'>
        {formatReminderTableDate(row.original.created_at)}
      </span>
    ),
  },
  {
    id: 'program',
    accessorFn: (row) => row.reminder.program?.name ?? '',
    header: 'Program',
    cell: ({ row }) => (
      <span className='line-clamp-2 max-w-[180px] text-sm text-gray-1000'>
        {row.original.reminder.program?.name ?? '—'}
      </span>
    ),
  },
  {
    id: 'recipient',
    accessorFn: (row) =>
      `${row?.recipient?.first_name ?? ''} ${row?.recipient?.last_name ?? ''}`.trim(),
    header: 'Recipient',
    cell: ({ row }) => {
      const { name, avatarUrl } = getRecipientDisplay(row.original);

      return (
        <div className='flex items-center gap-2'>
          <Avatar className='size-5 rounded-[4px]'>
            <AvatarImage src={avatarUrl} />
            <AvatarFallback>{name.slice(0, 1) || '?'}</AvatarFallback>
          </Avatar>
          <Text className='truncate text-sm'>{name}</Text>
        </div>
      );
    },
  },
  {
    id: 'recipientEmail',
    accessorFn: (row) => row.recipient?.email,
    header: 'Recipient Email',
    cell: ({ row }) => {
      const { recipient, lead } = row.original;

      return (
        <span className='line-clamp-1 max-w-[220px] text-sm text-gray-1000'>
          {recipient?.email || lead?.email || '—'}
        </span>
      );
    },
  },
  {
    id: 'whatsapp',
    accessorFn: (row) => row?.recipient?.whatsapp_number,
    header: 'WhatsApp Number',
    cell: ({ row }) => {
      const { recipient, lead } = row.original;

      return (
        <span className='whitespace-nowrap text-sm text-gray-1000'>
          {recipient?.whatsapp_number || lead?.whatsapp_number || '—'}
        </span>
      );
    },
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    enableSorting: false,
    cell: ({ row }) => <LogStatusBadge status={row.original.status} />,
  },
  {
    id: 'failureReason',
    accessorKey: 'failure_reason',
    header: 'Failure Reason',
    cell: ({ row }) => (
      <span className='text-sm text-gray-800'>
        {row.original.status === REMINDER_LOG_STATUSES.failed
          ? row.original.failure_reason?.trim() || '—'
          : '—'}
      </span>
    ),
  },
];
